import React, { useState } from 'react';
import './ResultCard.css';

const CATEGORY_PREVIEW_LIMIT = 2;
const EMPTY_CATEGORY_VALUE = '–';

const normalizeCategoryTitle = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value.title || null;
  return null;
};

const normalizeCategoryArray = (values) => {
  if (!Array.isArray(values)) return [];
  const unique = new Set();
  return values
    .map((entry) => normalizeCategoryTitle(entry))
    .filter((entry) => {
      if (!entry || unique.has(entry)) return false;
      unique.add(entry);
      return true;
    });
};

const formatPublicationDate = (value) => {
  if (!value || typeof value !== 'string') return '';

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${day}.${month}.${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  const day = String(parsed.getUTCDate()).padStart(2, '0');
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const year = parsed.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

const ResultCard = ({ image, title, showTitle, link, genreCategory, subgenreCategories, thematicCategories, publicationDate }) => {
  const [expandedCategories, setExpandedCategories] = useState(false);

  const genreTitle = normalizeCategoryTitle(genreCategory) || EMPTY_CATEGORY_VALUE;
  const subgenres = normalizeCategoryArray(subgenreCategories);
  const thematic = normalizeCategoryArray(thematicCategories);
  const formattedPublicationDate = formatPublicationDate(publicationDate);

  const hasExpandableContent = subgenres.length > CATEGORY_PREVIEW_LIMIT || thematic.length > CATEGORY_PREVIEW_LIMIT;

  const formatCategoryValues = (values) => {
    if (values.length === 0) {
      return EMPTY_CATEGORY_VALUE;
    }

    const visibleValues = expandedCategories ? values : values.slice(0, CATEGORY_PREVIEW_LIMIT);
    const baseText = visibleValues.join(', ');

    if (!expandedCategories && values.length > CATEGORY_PREVIEW_LIMIT) {
      return `${baseText}, ...`;
    }

    return baseText;
  };

  const onToggleCategories = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setExpandedCategories((prev) => !prev);
  };

  const renderToggle = () => {
    if (!hasExpandableContent) {
      return null;
    }

    return (
      <button
        type="button"
        className="card-category-toggle"
        onClick={onToggleCategories}
        aria-label={expandedCategories ? 'Weniger Kategorien anzeigen' : 'Alle Kategorien anzeigen'}
        title={expandedCategories ? 'Weniger Kategorien anzeigen' : 'Alle Kategorien anzeigen'}
      >
        <span aria-hidden="true">{expandedCategories ? '-' : '+'}</span>
      </button>
    );
  };

  return (
    <div className="card">
      <a href={link} target='_blank' rel="noreferrer">
          <div className="card-image-container">
              <img src={image} alt={title} className="image-bg" aria-hidden="true"/>
              <img src={image} alt={title} className="card-image"/>
          </div>
          <div className="card-show-title">{showTitle || ''}</div>
          <div className="card-title">{title}</div>
          {formattedPublicationDate && <div className="card-publication-date">{formattedPublicationDate}</div>}
          <div className="card-categories">
            <div className="card-category-line"><span className="card-category-label">Genre:</span> {genreTitle}</div>
            <div className="card-category-line"><span className="card-category-label">Subgenres:</span> {formatCategoryValues(subgenres)}</div>
            <div className="card-category-line"><span className="card-category-label">Themen:</span> {formatCategoryValues(thematic)}</div>
            {hasExpandableContent && (
              <div className="card-category-toggle-row">
                {renderToggle()}
              </div>
            )}
          </div>
      </a>
    </div>
  );
};

export default ResultCard;
